<template>
  <div class="video-slideshow-page">
    <v-container class="py-8">
      <!-- Header -->
      <div class="page-header mb-3">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
          </router-link>
        </div>

        <!-- Profile Menu (Top Right) -->
        <ProfileMenu />
      </div>

      <v-row justify="center">
        <v-col cols="12" lg="10">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-12">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <p class="text-h6 mt-4">Loading slide show...</p>
          </div>

          <!-- Video Settings Section -->
          <v-card v-if="!loading" class="main-card mb-6" elevation="0">
            <v-card-title class="text-h6 section-header primary--text">
              Video Settings
            </v-card-title>

            <v-card-text class="pa-6">
              <div class="settings-container">
                <div class="setting-item">
                  <label class="setting-label">Seconds between slides</label>
                  <v-text-field
                    v-model.number="slideDelay"
                    type="number"
                    min="1"
                    max="30"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="duration-input"
                  ></v-text-field>
                  <span class="duration-info text-body-2 text--secondary ml-4">
                    Video will be approximately {{ videoDuration }}
                  </span>
                </div>

                <div class="setting-item">
                  <label class="setting-label">Show captions</label>
                  <v-switch
                    v-model="showCaptions"
                    color="primary"
                    hide-details
                    density="compact"
                  ></v-switch>
                </div>

                <div class="setting-item setting-item--stacked" v-if="showCaptions">
                  <div class="setting-item-row">
                    <label class="setting-label">Truncate captions to (words)</label>
                    <v-text-field
                      v-model.number="captionWordLimit"
                      type="number"
                      min="0"
                      max="50"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="duration-input"
                    ></v-text-field>
                  </div>
                  <p class="setting-hint text-caption">
                    <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
                    There won't be much time to read text in the video — keep captions short (5–8 words works best). Set to 0 to hide all captions.
                  </p>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Slides Section -->
          <v-card v-if="!loading" class="main-card" elevation="0">
            <v-card-title class="text-h6 section-header primary--text">
              Slides ({{ slides.length }})
            </v-card-title>

            <v-card-text class="pa-6">
              <p class="text-body-2 text--secondary mb-4">
                Drag and drop to reorder slides. Click a slide to view full size.
              </p>

              <div v-if="slides.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1" class="mb-3">mdi-image-off</v-icon>
                <p class="text-body-1 text--secondary">No photos available for slideshow</p>
              </div>

              <div v-else class="slides-grid">
                <div
                  v-for="(slide, index) in slides"
                  :key="slide.uuid"
                  class="slide-item"
                  :draggable="true"
                  @dragstart="handleDragStart($event, index)"
                  @dragover="handleDragOver($event)"
                  @drop="handleDrop($event, index)"
                  @dragenter="handleDragEnter($event, index)"
                  @dragleave="handleDragLeave($event)"
                  @click="openPhotoDialog(slide)"
                >
                  <div class="slide-thumbnail">
                    <img :src="slide.url" :alt="slide.caption || 'Slide'" />
                    <div class="slide-overlay">
                      <v-icon color="white">mdi-eye</v-icon>
                    </div>
                    <div class="slide-number">{{ index + 1 }}</div>
                  </div>
                  <div v-if="showCaptions && truncateCaption(slide.caption)" class="slide-caption">
                    {{ truncateCaption(slide.caption) }}
                  </div>
                </div>
              </div>
            </v-card-text>

            <!-- Save feedback messages -->
            <v-card-text class="pa-6 pt-0" v-if="saveMessage">
              <v-alert
                :type="saveMessageType"
                variant="tonal"
                closable
                @click:close="saveMessage = ''"
              >
                {{ saveMessage }}
              </v-alert>
            </v-card-text>

            <!-- Settings required note -->
            <v-card-text class="pa-6 pt-0" v-if="!hasSavedSettings">
              <v-alert type="info" variant="tonal">
                Save slide show settings before generating the video.
              </v-alert>
            </v-card-text>

            <!-- Video Generation Status -->
            <v-card-text class="pa-6 pt-0" v-if="videoStatus">
              <v-alert
                v-if="videoStatus === 'PROCESSING'"
                type="info"
                variant="tonal"
              >
                <v-progress-circular
                  indeterminate
                  color="info"
                  size="20"
                  width="2"
                  class="mr-2"
                ></v-progress-circular>
                Video is being generated... This may take a few minutes.
              </v-alert>

              <v-alert
                v-if="videoStatus === 'COMPLETED' && videoUrl"
                type="success"
                variant="tonal"
              >
                <div class="video-success-row">
                  <span>Video generated successfully!</span>
                  <v-btn
                    :href="videoUrl"
                    target="_blank"
                    download
                    color="success"
                    variant="tonal"
                    prepend-icon="mdi-download"
                  >
                    Download Video
                  </v-btn>
                </div>
              </v-alert>

              <v-alert
                v-if="videoStatus === 'FAILED'"
                type="error"
                variant="tonal"
              >
                Video generation failed. {{ errorMessage || 'Please try again.' }}
              </v-alert>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0 bottom-actions">
              <v-btn
                variant="text"
                @click="goToDashboard"
              >
                Back to Dashboard
              </v-btn>
              <v-spacer class="d-none d-sm-block"></v-spacer>
              <v-btn
                color="primary"
                prepend-icon="mdi-content-save"
                variant="outlined"
                class="action-btn"
                @click="saveSlideShowSettings"
                :loading="saving"
                :disabled="saving || videoStatus === 'PROCESSING'"
              >
                Save Slide Show Settings
              </v-btn>
              <v-btn
                color="primary"
                prepend-icon="mdi-video"
                variant="outlined"
                class="action-btn"
                @click="generateVideo"
                :loading="generating"
                :disabled="generating || videoStatus === 'PROCESSING' || !hasSavedSettings"
              >
                {{ generating || videoStatus === 'PROCESSING' ? 'Generating...' : 'Generate Video' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Photo Dialog -->
    <v-dialog v-model="photoDialog" max-width="900">
      <v-card v-if="selectedPhoto">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Photo {{ selectedPhotoIndex + 1 }} of {{ slides.length }}</span>
          <v-btn icon variant="text" @click="photoDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <img :src="selectedPhoto.url" :alt="selectedPhoto.caption || 'Photo'" class="dialog-photo" />
          <div class="pa-4">
            <v-textarea
              v-model="editingCaption"
              variant="outlined"
              rows="3"
              auto-grow
              hide-details
              placeholder="Enter caption for this slide..."
              @input="updateCaption"
            ></v-textarea>
            <div class="d-flex justify-space-between align-center mt-2">
              <p class="text-caption text--secondary">
                Changes to captions are only for this slideshow and won't be saved to the memorial
              </p>
              <v-btn
                variant="text"
                size="small"
                @click="revertToOriginalCaption"
              >
                Revert to Original Caption
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="text"
            :disabled="selectedPhotoIndex === 0"
            @click="previousPhoto"
          >
            <v-icon left>mdi-chevron-left</v-icon>
            Previous
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            :disabled="selectedPhotoIndex === slides.length - 1"
            @click="nextPhoto"
          >
            Next
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import ProfileMenu from '../components/ProfileMenu.vue'

const router = useRouter()
const store = useMemorialStore()

// Refs
const memorial = ref(null)
const contributions = ref([])

// Settings
const slideDelay = ref(5)
const showCaptions = ref(true)
const captionWordLimit = ref(15)

// Slides
const slides = ref([])
const originalCaptions = ref({}) // Store original captions by slide uuid

// Drag and drop
const draggedIndex = ref(null)

// Photo dialog
const photoDialog = ref(false)
const selectedPhoto = ref(null)
const selectedPhotoIndex = ref(0)
const editingCaption = ref('')

// Saving state
const saving = ref(false)
const saveMessage = ref('')
const saveMessageType = ref('success')
const hasSavedSettings = ref(false)
const savedSlidesCache = ref(null)
const extraSlideShowMedia = ref([])

// Loading state
const loading = ref(true)

// Video generation state
const generating = ref(false)
const videoStatus = ref(null)
const videoUrl = ref(null)
const errorMessage = ref(null)
let statusPollInterval = null

// Computed video duration
const videoDuration = computed(() => {
  // Total slides = user slides + 2 (title slide + end slide)
  const totalSlides = slides.value.length + 2
  const totalSeconds = totalSlides * slideDelay.value

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes > 0) {
    return `${minutes} min ${seconds} sec`
  } else {
    return `${seconds} sec`
  }
})

// Load slideshow data if it exists
const loadSlideShowData = async () => {
  try {
    const authToken = store.apiToken
    if (!authToken || !store.memorialUuid) {
      return
    }

    const response = await apiService.getSlideShow(store.memorialUuid, authToken)
    if (response.success && response.slideShow && response.slideShow.slideInfo) {
      hasSavedSettings.value = true
      savedSlidesCache.value = null
      extraSlideShowMedia.value = response.slideShow.allMedia || []
      // Parse the JSON slideInfo
      const slideInfo = JSON.parse(response.slideShow.slideInfo)

      // Apply settings
      if (slideInfo.settings) {
        slideDelay.value = slideInfo.settings.slideDelay || 5
        showCaptions.value = slideInfo.settings.showCaptions !== undefined ? slideInfo.settings.showCaptions : true
        captionWordLimit.value = slideInfo.settings.captionWordLimit !== undefined ? slideInfo.settings.captionWordLimit : 15
      }

      // Store saved slides order
      savedSlidesCache.value = slideInfo.slides || []
      return savedSlidesCache.value
    }
  } catch (error) {
    console.error('Error loading slideshow data:', error)
  }
  hasSavedSettings.value = false
  savedSlidesCache.value = null
  extraSlideShowMedia.value = []
  return null
}

// Load memorial and contribution data
const loadMemorialData = async () => {
  try {
    const response = await apiService.getMemorial(store.memorialUuid)
    if (response.success && response.memorial) {
      memorial.value = response.memorial
      buildSlides()
    }
  } catch (error) {
    console.error('Error loading memorial data:', error)
  }
}

const loadContributions = async () => {
  try {
    const response = await apiService.getPublicMemorial(store.shareSlug || memorial.value?.shareSlug)
    if (response.success && response.contributions) {
      contributions.value = response.contributions
      buildSlides()
    }
  } catch (error) {
    console.error('Error loading contributions:', error)
  }
}

const buildSlides = async (savedSlides = null, extraMedia = null) => {
  const effectiveSavedSlides = savedSlides ?? savedSlidesCache.value
  const effectiveExtraMedia = extraMedia ?? extraSlideShowMedia.value
  const allSlides = []

  // Add memorial profile photo first
  if (memorial.value?.profilePhoto) {
    const profile = memorial.value.profilePhoto
    const caption = profile.title || memorial.value.nameOfPerson || ''
    allSlides.push({
      uuid: profile.uuid,
      url: profile.location
        ? `${profile.location}/${profile.filename}.${profile.extension}`
        : `https://assets.forever-x.com/${profile.filename}.${profile.extension}`,
      caption: caption,
      source: 'profile'
    })
    originalCaptions.value[profile.uuid] = caption
  }

  // Add memorial photos
  if (memorial.value?.media) {
    memorial.value.media.filter(media => media.mediaType === 0 || media.mediaType == null).forEach(media => {
      const caption = media.title || ''
      allSlides.push({
        uuid: media.uuid,
        url: media.location
          ? `${media.location}/${media.filename}.${media.extension}`
          : `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`,
        caption: caption,
        source: 'memorial'
      })
      // Store original caption
      originalCaptions.value[media.uuid] = caption
    })
  }

  // Add contribution photos
  const contributionSources = contributions.value?.length ? contributions.value : (memorial.value?.contributions || [])
  if (contributionSources && contributionSources.length > 0) {
    const approvedContributions = contributionSources.filter(contribution => !!contribution.dateApproved)
    approvedContributions.forEach(contribution => {
      if (contribution.media) {
        contribution.media.filter(media => media.mediaType === 0 || media.mediaType == null).forEach(media => {
          const caption = media.title || ''
          allSlides.push({
            uuid: media.uuid,
            url: media.location
              ? `${media.location}/${media.filename}.${media.extension}`
              : `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`,
            caption: caption,
            source: 'contribution'
          })
          // Store original caption
          originalCaptions.value[media.uuid] = caption
        })
      }
    })
  }

  if (effectiveExtraMedia && effectiveExtraMedia.length > 0) {
    const existingUuids = new Set(allSlides.map(slide => slide.uuid))
    effectiveExtraMedia.forEach(media => {
      if (!media?.uuid || existingUuids.has(media.uuid)) return
      const caption = media.title || ''
      allSlides.push({
        uuid: media.uuid,
        url: media.location
          ? `${media.location}/${media.filename}.${media.extension}`
          : `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`,
        caption: caption,
        source: media.source || 'memorial'
      })
      originalCaptions.value[media.uuid] = caption
      existingUuids.add(media.uuid)
    })
  }

  // If we have saved slides data, reorder and apply saved captions
  if (effectiveSavedSlides && effectiveSavedSlides.length > 0) {
    const reorderedSlides = []
    const slideMap = new Map(allSlides.map(slide => [slide.uuid, slide]))
    const savedUuids = new Set(effectiveSavedSlides.map(savedSlide => savedSlide.uuid))

    // Apply saved order and captions
    effectiveSavedSlides.forEach(savedSlide => {
      const slide = slideMap.get(savedSlide.uuid)
      if (slide) {
        slide.caption = savedSlide.caption || slide.caption
        reorderedSlides.push(slide)
        slideMap.delete(savedSlide.uuid)
      }
    })

    // Add any new slides that weren't in the saved order (append to the end)
    const missingSlides = allSlides.filter(slide => !savedUuids.has(slide.uuid))
    reorderedSlides.push(...missingSlides)

    slides.value = reorderedSlides
  } else {
    slides.value = allSlides
  }
}

// Drag and Drop handlers (vanilla JS)
const handleDragStart = (event, index) => {
  draggedIndex.value = index
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/html', event.target.innerHTML)
  event.target.classList.add('dragging')
}

const handleDragOver = (event) => {
  event.preventDefault()
  event.dataTransfer.dropEffect = 'move'
}

const handleDragEnter = (event, index) => {
  if (draggedIndex.value !== null && draggedIndex.value !== index) {
    event.target.closest('.slide-item')?.classList.add('drag-over')
  }
}

const handleDragLeave = (event) => {
  event.target.closest('.slide-item')?.classList.remove('drag-over')
}

const handleDrop = (event, dropIndex) => {
  event.preventDefault()
  event.target.closest('.slide-item')?.classList.remove('drag-over')

  const items = event.target.closest('.slide-item')?.parentNode.querySelectorAll('.slide-item')
  items?.forEach(item => item.classList.remove('dragging'))

  if (draggedIndex.value !== null && draggedIndex.value !== dropIndex) {
    const draggedSlide = slides.value[draggedIndex.value]
    const newSlides = [...slides.value]

    // Remove from old position
    newSlides.splice(draggedIndex.value, 1)

    // Insert at new position
    newSlides.splice(dropIndex, 0, draggedSlide)

    slides.value = newSlides
  }

  draggedIndex.value = null
}

// Photo dialog handlers
const openPhotoDialog = (slide) => {
  selectedPhoto.value = slide
  selectedPhotoIndex.value = slides.value.findIndex(s => s.uuid === slide.uuid)
  editingCaption.value = slide.caption || ''
  photoDialog.value = true
}

const updateCaption = () => {
  // Update the caption in the slides array (not saved to API)
  if (selectedPhoto.value) {
    slides.value[selectedPhotoIndex.value].caption = editingCaption.value
    selectedPhoto.value.caption = editingCaption.value
  }
}

const previousPhoto = () => {
  if (selectedPhotoIndex.value > 0) {
    selectedPhotoIndex.value--
    selectedPhoto.value = slides.value[selectedPhotoIndex.value]
    editingCaption.value = selectedPhoto.value.caption || ''
  }
}

const nextPhoto = () => {
  if (selectedPhotoIndex.value < slides.value.length - 1) {
    selectedPhotoIndex.value++
    selectedPhoto.value = slides.value[selectedPhotoIndex.value]
    editingCaption.value = selectedPhoto.value.caption || ''
  }
}

const truncateCaption = (text) => {
  if (!text) return ''
  if (captionWordLimit.value === 0) return ''
  const words = text.trim().split(/\s+/)
  if (words.length <= captionWordLimit.value) return text
  return words.slice(0, captionWordLimit.value).join(' ') + '...'
}

const revertToOriginalCaption = () => {
  if (selectedPhoto.value) {
    const originalCaption = originalCaptions.value[selectedPhoto.value.uuid] || ''
    editingCaption.value = originalCaption
    slides.value[selectedPhotoIndex.value].caption = originalCaption
    selectedPhoto.value.caption = originalCaption
  }
}


const saveSlideShowSettings = async () => {
  try {
    saving.value = true
    saveMessage.value = ''

    const authToken = store.apiToken
    if (!authToken) {
      console.error('No auth token available')
      saveMessage.value = 'You must be logged in to save slide show settings'
      saveMessageType.value = 'error'
      return
    }

    // Prepare settings data
    const settings = {
      slideDelay: slideDelay.value,
      showCaptions: showCaptions.value,
      captionWordLimit: captionWordLimit.value
    }

    // Prepare slides data (with order and captions)
    const slidesData = slides.value.map((slide, index) => ({
      uuid: slide.uuid,
      order: index,
      caption: slide.caption || '',
      url: slide.url,
      source: slide.source
    }))

    // Save to API
    const response = await apiService.saveSlideShow(
      store.memorialUuid,
      settings,
      slidesData,
      authToken
    )

    if (response.success) {
      console.log('Slideshow saved successfully:', response.slideShow)
      saveMessage.value = 'Slide show settings saved successfully!'
      saveMessageType.value = 'success'
      hasSavedSettings.value = true
    } else {
      console.error('Failed to save slideshow:', response.msg)
      saveMessage.value = response.msg || 'Failed to save slide show settings'
      saveMessageType.value = 'error'
    }
  } catch (error) {
    console.error('Error saving slideshow:', error)
    saveMessage.value = 'Error saving slide show settings: ' + (error.message || 'Unknown error')
    saveMessageType.value = 'error'
  } finally {
    saving.value = false
  }
}

const generateVideo = async () => {
  try {
    generating.value = true
    saveMessage.value = ''

    const authToken = store.apiToken
    if (!authToken) {
      saveMessage.value = 'You must be logged in to generate video'
      saveMessageType.value = 'error'
      return
    }

    const response = await apiService.generateSlideShowVideo(store.memorialUuid, authToken)

    if (response.success) {
      videoStatus.value = response.slideShow.status
      saveMessage.value = 'Video generation started!'
      saveMessageType.value = 'success'

      // Start polling for status
      startStatusPolling()
    } else {
      saveMessage.value = response.msg || 'Failed to start video generation'
      saveMessageType.value = 'error'
    }
  } catch (error) {
    console.error('Error generating video:', error)
    saveMessage.value = 'Error starting video generation: ' + (error.message || 'Unknown error')
    saveMessageType.value = 'error'
  } finally {
    generating.value = false
  }
}

const STATUS_POLL_MAX_MS = 10 * 60 * 1000 // stop polling after 10 minutes so the UI can't spin forever

const startStatusPolling = () => {
  // Clear any existing interval
  if (statusPollInterval) {
    clearInterval(statusPollInterval)
  }

  const pollStartedAt = Date.now()

  // Poll every second
  statusPollInterval = setInterval(async () => {
    if (Date.now() - pollStartedAt > STATUS_POLL_MAX_MS) {
      clearInterval(statusPollInterval)
      statusPollInterval = null
      videoStatus.value = 'FAILED'
      errorMessage.value = 'Video generation is taking longer than expected. Please refresh the page to check again, or try generating the video later.'
      return
    }
    await checkVideoStatus()
  }, 1000)
}

const checkVideoStatus = async () => {
  try {
    const authToken = store.apiToken
    if (!authToken) return

    const response = await apiService.checkSlideShowStatus(store.memorialUuid, authToken)

    if (response.success && response.slideShow) {
      videoStatus.value = response.slideShow.status

      if (response.slideShow.status === 'COMPLETED') {
        saveMessage.value = ''
        videoUrl.value = response.slideShow.videoLocation
        errorMessage.value = null
        // Stop polling
        if (statusPollInterval) {
          clearInterval(statusPollInterval)
          statusPollInterval = null
        }
      } else if (response.slideShow.status === 'FAILED') {
        saveMessage.value = ''
        errorMessage.value = response.slideShow.errorMessage
        // Stop polling
        if (statusPollInterval) {
          clearInterval(statusPollInterval)
          statusPollInterval = null
        }
      }
    }
  } catch (error) {
    console.error('Error checking video status:', error)
  }
}

const goToDashboard = () => {
  router.push('/memory/dashboard')
}

onMounted(async () => {
  try {
    loading.value = true

    // First load the slideshow data to get settings and saved order
    const savedSlides = await loadSlideShowData()

    // Then load memorial and contribution data
    await loadMemorialData()
    if (store.shareSlug || memorial.value?.shareSlug) {
      await loadContributions()
    }

    // Build slides with saved order if available
    buildSlides(savedSlides)

    // Check initial video status
    await checkVideoStatus()

    // If video is processing, start polling
    if (videoStatus.value === 'PROCESSING') {
      startStatusPolling()
    }
  } catch (error) {
    console.error('Error loading page data:', error)
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  // Clean up polling interval
  if (statusPollInterval) {
    clearInterval(statusPollInterval)
    statusPollInterval = null
  }
})
</script>

<style scoped>
.video-slideshow-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
}

.page-header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.5rem;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.profile-menu-wrapper {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.main-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-header {
  font-weight: 600 !important;
  border-bottom: 1px solid var(--v-theme-border);
}

.settings-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-item {
  display: flex;
  align-items: center;
  gap: 16px;
}

.setting-item .v-input {
  align-self: center;
}

.setting-item--stacked {
  flex-direction: column;
  align-items: flex-start;
}

.setting-item-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.setting-hint {
  color: var(--v-theme-gray500);
  margin-top: 4px;
  display: flex;
  align-items: flex-start;
}

.setting-label {
  font-weight: 500;
  font-size: 14px;
  color: var(--v-theme-gray600);
  min-width: 200px;
}

.duration-input {
  max-width: 64px;
  margin-top: 0;
}

.video-success-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

@media (max-width: 600px) {
  .setting-item {
    flex-wrap: wrap;
  }

  .duration-info {
    flex-basis: 100%;
    margin-left: 0 !important;
  }

  .video-success-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .bottom-actions {
    flex-wrap: wrap;
    gap: 8px;
  }

  .bottom-actions .action-btn {
    flex: 1 1 100%;
    margin: 0 !important;
  }
}

.slides-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.slide-item {
  width: 150px;
  cursor: move;
  transition: transform 0.2s, opacity 0.2s;
  position: relative;
}

.slide-item:hover {
  transform: translateY(-4px);
}

.slide-item.dragging {
  opacity: 0.5;
}

.slide-item.drag-over {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(127, 203, 174, 0.3);
}

.slide-thumbnail {
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--v-theme-gray100);
  border: 2px solid var(--v-theme-neutral200);
}

.slide-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.slide-item:hover .slide-overlay {
  opacity: 1;
}

.slide-number {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.slide-caption {
  margin-top: 8px;
  font-size: 12px;
  color: var(--v-theme-gray600);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.dialog-photo {
  width: 100%;
  height: auto;
  max-height: 50vh;
  object-fit: contain;
  background: var(--v-theme-black);
}

.action-btn {
  background-color: var(--v-theme-surface);
}
</style>
